import { endOfDay, startOfDay, subDays } from "date-fns";
import {
  ComparisonPeriod,
  DateRange,
  FilterQueryParams,
  FilterState,
  PeriodValue,
  formatDateToISO,
} from "./filter.types";

const periodOptions: Array<{ value: PeriodValue; label: string }> = [
  { value: "today", label: "Hoje" },
  { value: "yesterday", label: "Ontem" },
  { value: "24h", label: "Últimas 24 horas" },
  { value: "7d", label: "Últimos 7 dias" },
  { value: "14d", label: "14 dias" },
  { value: "30d", label: "30 dias" },
  { value: "custom", label: "Período personalizado" },
];

export const comparisonOptions: ComparisonPeriod[] = [
  { type: "none", label: "Sem comparação" },
  { type: "previous", label: "Prazo anterior" },
  { type: "previousMonth", label: "Mês anterior" },
  { type: "previousYear", label: "Ano anterior" },
];

export const calculateDateRange = (
  selectedPeriod: PeriodValue,
  customStartDate?: string,
  customEndDate?: string,
): DateRange => {
  const now = new Date();
  const today = startOfDay(now);

  switch (selectedPeriod) {
    case "today":
      return {
        startDate: today,
        endDate: endOfDay(now),
      };
    case "yesterday": {
      const y = subDays(today, 1);
      return {
        startDate: startOfDay(y),
        endDate: endOfDay(y),
      };
    }
    case "24h":
      return {
        startDate: subDays(now, 1),
        endDate: now,
      };
    case "7d":
      return {
        // "Últimos 7 dias" sem contar o dia atual: D-7 até D-1
        startDate: startOfDay(subDays(now, 7)),
        endDate: endOfDay(subDays(now, 1)),
      };
    case "14d":
      return {
        // "14 dias" sem contar o dia atual: D-14 até D-1
        startDate: startOfDay(subDays(now, 14)),
        endDate: endOfDay(subDays(now, 1)),
      };
    case "30d":
      return {
        // "30 dias" sem contar o dia atual: D-30 até D-1
        startDate: startOfDay(subDays(now, 30)),
        endDate: endOfDay(subDays(now, 1)),
      };
    case "custom":
      // Período personalizado: respeitar exatamente as datas que o usuário preencheu
      // Criar datas no fuso horário local para evitar bug de timezone
      if (customStartDate && customEndDate) {
        const [startYear, startMonth, startDay] = customStartDate
          .split("-")
          .map(Number);
        const [endYear, endMonth, endDay] = customEndDate
          .split("-")
          .map(Number);
        return {
          startDate: startOfDay(new Date(startYear, startMonth - 1, startDay)),
          endDate: endOfDay(new Date(endYear, endMonth - 1, endDay)),
        };
      }
      // Fallback se não houver datas personalizadas definidas
      return {
        startDate: startOfDay(subDays(now, 7)),
        endDate: endOfDay(subDays(now, 1)),
      };
    default:
      return {
        startDate: startOfDay(subDays(now, 7)),
        endDate: endOfDay(subDays(now, 1)),
      };
  }
};

export const getPeriodLabel = (selectedPeriod: PeriodValue): string => {
  if (selectedPeriod === "custom") {
    return "Período personalizado";
  }

  const option = periodOptions.find(
    (option) => option.value === selectedPeriod,
  );
  return option ? option.label : "Últimos 7 dias";
};

const normalizeFilterState = (filter: FilterState): FilterState => {
  const comparison =
    filter.comparison?.type === "none" ? null : filter.comparison;
  return {
    ...filter,
    comparison,
  };
};

export const buildFilterQueryParams = (
  filter: FilterState,
): FilterQueryParams => {
  const normalized = normalizeFilterState(filter);
  const effectiveRange =
    normalized.period === "custom" && normalized.customDateRange
      ? normalized.customDateRange
      : normalized.dateRange;

  const params: FilterQueryParams = {
    start_date: formatDateToISO(effectiveRange.startDate),
    end_date: formatDateToISO(effectiveRange.endDate),
    period: normalized.period,
  };

  if (normalized.comparison) {
    params.comparison_type = normalized.comparison.type;
    params.comparison_label = normalized.comparison.label;
  }

  if (normalized.customDateRange) {
    params.custom_start_date = formatDateToISO(
      normalized.customDateRange.startDate,
    );
    params.custom_end_date = formatDateToISO(
      normalized.customDateRange.endDate,
    );
  }

  return params;
};

export const filtersAreEqual = (
  a: FilterState | null,
  b: FilterState | null,
): boolean => {
  if (!a || !b) {
    return false;
  }

  if (a.period !== b.period) {
    return false;
  }

  const comparisonA = a.comparison?.type ?? "none";
  const comparisonB = b.comparison?.type ?? "none";
  if (comparisonA !== comparisonB) {
    return false;
  }

  if (
    a.dateRange.startDate.toISOString() !== b.dateRange.startDate.toISOString()
  ) {
    return false;
  }

  if (a.dateRange.endDate.toISOString() !== b.dateRange.endDate.toISOString()) {
    return false;
  }

  const hasCustomA = Boolean(a.customDateRange);
  const hasCustomB = Boolean(b.customDateRange);
  if (hasCustomA !== hasCustomB) {
    return false;
  }

  if (hasCustomA && hasCustomB) {
    if (
      a.customDateRange!.startDate.toISOString() !==
      b.customDateRange!.startDate.toISOString()
    ) {
      return false;
    }
    if (
      a.customDateRange!.endDate.toISOString() !==
      b.customDateRange!.endDate.toISOString()
    ) {
      return false;
    }
  }

  return true;
};

const ensureFilterIntegrity = (filter: FilterState): FilterState => {
  const normalized = normalizeFilterState(filter);
  const dateRange = calculateDateRange(normalized.period);
  return {
    ...normalized,
    dateRange: {
      startDate: new Date(normalized.dateRange.startDate),
      endDate: new Date(normalized.dateRange.endDate),
    },
    ...(normalized.customDateRange && {
      customDateRange: {
        startDate: new Date(normalized.customDateRange.startDate),
        endDate: new Date(normalized.customDateRange.endDate),
      },
    }),
  };
};

export const getComparisonOption = (
  type: ComparisonPeriod["type"],
): ComparisonPeriod => {
  return (
    comparisonOptions.find((option) => option.type === type) ??
    comparisonOptions[0]
  );
};

/**
 * Retorna se o modo de comparação está ativo (ou seja, não é "Sem comparação").
 */
export const isComparisonEnabled = (
  filter: FilterState | null | undefined,
): boolean => {
  const t = filter?.comparison?.type ?? "none";
  return t !== "none";
};
